import React, { useRef, useState } from "react";
import { LuCamera, LuImage } from "react-icons/lu";
import { colors } from "./constants";
import { prepareImageForUpload } from "./utils";

// The app's photo control.
//
// Two buttons, not one, because a single <input> cannot offer both. An input
// carrying `capture` opens the camera and nothing else; one without it opens
// the library (on iOS, via an action sheet that asks which you wanted). A tech
// standing in front of a failed asset should reach the camera in one tap, and
// still be able to attach something shot earlier — so both are on screen and
// neither is a guess.
//
// Everything is routed through prepareImageForUpload before the caller sees
// it. That matters more than it looks: an iPhone library photo is usually
// HEIC, which Pillow rejects outright, so paths that skipped this were not
// merely slow — they failed.
export function PhotoInput({
  onFiles,
  multiple = false,
  disabled = false,
  busy = false,
  compact = false,
  takeLabel = "Take photo",
  libraryLabel = "Library",
}) {
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [preparing, setPreparing] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    // Cleared so the same file can be chosen twice in a row — retrying after a
    // failed upload otherwise does nothing.
    e.target.value = "";
    if (files.length === 0) return;

    setPreparing(true);
    try {
      const prepared = [];
      for (const file of files) {
        prepared.push(await prepareImageForUpload(file));
      }
      await onFiles(prepared);
    } finally {
      setPreparing(false);
    }
  };

  const working = busy || preparing;
  const blocked = disabled || working;

  const button = (ref, icon, label) => (
    <button
      type="button"
      onClick={() => ref.current?.click()}
      disabled={blocked}
      style={{
        flex: compact ? "none" : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        // Comfortably past the 44px touch target a phone wants.
        padding: compact ? "10px 12px" : "12px 14px",
        borderRadius: "8px",
        border: `1px solid ${colors.border}`,
        backgroundColor: blocked ? "#f8fafc" : colors.white,
        color: blocked ? colors.slate : colors.text,
        fontSize: "13px",
        fontWeight: "600",
        cursor: blocked ? "default" : "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        {button(
          cameraRef,
          <LuCamera size={15} />,
          working ? "Working..." : takeLabel
        )}
        {button(libraryRef, <LuImage size={15} />, libraryLabel)}
      </div>

      {/* capture="environment" asks for the rear camera directly, which is
          what removes the extra "Take Photo or Video" tap on iOS. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handle}
        style={{ display: "none" }}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handle}
        style={{ display: "none" }}
      />
    </div>
  );
}
